import axios from 'axios'
import AxiosInterceptor from '../hooks/AxiosInterceptorHook'
import Cookies from 'js-cookie'
import {apiURLs, AddPDPCookie, getWithCredentials} from './Utility'
import {getCsrfToken as getStoredCsrfToken} from './csrfToken'

const csrfCookieNames = ['XSRF-TOKEN', 'CSRF-TOKEN', 'X-CSRF-TOKEN']

const getCsrfToken = () => {
  const storedToken = getStoredCsrfToken()
  if(storedToken) {
    return storedToken
  }

  for(let i = 0; i < csrfCookieNames.length; i += 1) {
    const token = Cookies.get(csrfCookieNames[i])
    if(token) {
      return token
    }
  }

  return null
}

const withCsrfHeader = (request = {}) => {
  const method = (request.method || 'GET').toUpperCase()
  if(!['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    return request
  }

  const csrfToken = getCsrfToken()
  if(!csrfToken) {
    return Promise.reject(new Error('CSRF token not found. Unable to send request.'))
  }

  return {
    ...request,
    headers: {
      ...(request.headers || {}),
      'X-CSRF-Token': csrfToken,
      'X-XSRF-TOKEN': csrfToken
    }
  }
}

const generateCorrelationId = () => (typeof crypto !== 'undefined' && crypto.randomUUID)
  ? crypto.randomUUID()
  : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`

const withCorrelationHeader = (request = {}) => ({
  ...request,
  headers: {
    ...(request.headers || {}),
    'X-Correlation-ID': generateCorrelationId()
  }
})

const createBranchApiAxiosInstance = () => {
  const axiosInstance = axios.create({
    baseURL: apiURLs.branchApiBaseUrl,
    headers: {
      'Content-Type': 'application/json'
    },
    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',
    withCredentials: getWithCredentials()
  })

  AddPDPCookie(axiosInstance)
  axiosInstance.interceptors.request.use((request) => {
    try {
      return withCorrelationHeader(request)
    } catch(error) {
      console.error('Failed to add correlation header:', error)
      return request
    }
  })
  axiosInstance.interceptors.request.use(withCsrfHeader)

  const interceptor = AxiosInterceptor(axiosInstance)
  return {axiosInstance, interceptor}
}

const branchApiAxiosInstance = createBranchApiAxiosInstance()

export default {
  getBranch: (branchId, status) => branchApiAxiosInstance.axiosInstance({
    method: 'GET',
    url: `/api/v1/branch/${encodeURIComponent(branchId)}`,
    params: status ? {status} : undefined
  }),

  updateBranch: (branchId, branch) => branchApiAxiosInstance.axiosInstance({
    method: 'PUT',
    url: `/api/v1/branch/${encodeURIComponent(branchId)}`,
    data: branch
  }),

  loadBranches: () => branchApiAxiosInstance.axiosInstance({
    method: 'POST',
    url: '/api/v1/branch/load'
  }),

  setJwtToken: jwtToken => {
    branchApiAxiosInstance.axiosInstance.defaults.headers.common.Authorization = jwtToken
      ? `Bearer ${jwtToken}`
      : ''
  },

  setRequestInterceptor: interceptor => {
    branchApiAxiosInstance.interceptor.setRequestInterceptor(interceptor)
  },

  setResponseInterceptor: (interceptor, onRejected) => {
    branchApiAxiosInstance.interceptor.setResponseInterceptor(interceptor, onRejected)
  }
}
